import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import {
  Stethoscope, Sparkles, AlertTriangle, ShieldAlert, ShieldCheck, Info,
  BookOpen, FlaskConical, Pill, ArrowRight, Loader2, Ban, Plus, ShieldOff, History, X, ExternalLink, FileText,
} from "lucide-react";
import { runClinicalRules, LEVEL_ORDER } from "../lib/clinicalRules";
import { isInsulinRow, insulinConcentration, isUnitDoseAsStrength, isCombinedProductName, parseAmount } from "../lib/doseCompat";
import { suggestInsulin, suggestGLP1, suggestAllPushDrugs } from "../lib/doseAlgorithms";
import { suggestAllChronicConditions, CHRONIC_ORDER } from "../lib/chronicConditions";
import { adjustRxForContext } from "../lib/prescribeSafety";
import { ckdEpi2021 } from "./ClinicalCalculators";
import DrugEvidencePopover from "./DrugEvidencePopover";

const LEVEL_STYLE = {
  block: { cls: "bg-red-50 border-red-200 text-red-800", Icon: Ban, chip: "bg-red-600 text-white" },
  major: { cls: "bg-orange-50 border-orange-200 text-orange-800", Icon: ShieldAlert, chip: "bg-orange-600 text-white" },
  caution: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: AlertTriangle, chip: "bg-amber-500 text-white" },
  info: { cls: "bg-sky-50 border-sky-200 text-sky-800", Icon: Info, chip: "bg-sky-600 text-white" },
};

// Resolve the guideline citation text (e.g. "ADA 2026 §11.5") to a reference
// URL from the doctor-editable guidelines list. Matches by short name
// (case-insensitive) at the start of the citation, tolerant of "AHA/ACC" etc.
function resolveGuidelineUrl(citation, guidelines) {
  if (!citation || !guidelines || !guidelines.length) return "";
  const c = String(citation).toLowerCase();
  // Prefer longest name so "AHA/ACC" wins over "AHA"
  const sorted = [...guidelines].filter((g) => g.url).sort((a, b) => (b.name || "").length - (a.name || "").length);
  for (const g of sorted) {
    const n = String(g.name || "").toLowerCase().trim();
    if (!n) continue;
    if (c.includes(n)) return g.url;
  }
  return "";
}

function GuidelineLink({ text, url, testid }) {
  if (!text) return null;
  const inner = (
    <span className="inline-flex items-center gap-1"><BookOpen size={10} /> {text}</span>
  );
  if (!url) return <div className="text-[10px] opacity-75 mt-0.5 flex items-center gap-1">{inner}</div>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[10px] opacity-90 hover:opacity-100 mt-0.5 flex items-center gap-1 text-pink-700 hover:text-pink-800 hover:underline"
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
    >
      {inner}
      <ExternalLink size={9} />
    </a>
  );
}

function AlertRow({ a, overridden, onOverride, guidelineUrl }) {
  const s = LEVEL_STYLE[a.level] || LEVEL_STYLE.info;
  const Icon = s.Icon;
  const isBlockOrMajor = a.level === "block" || a.level === "major";
  return (
    <div className={`border rounded-md px-3 py-2 text-xs flex items-start gap-2 ${s.cls} ${overridden ? "opacity-50" : ""}`} data-testid={`guidance-alert-${a.rule_id || "ai"}`}>
      <Icon size={14} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${s.chip}`}>{a.level}</span>
          <span className={`font-medium ${overridden ? "line-through" : ""}`}>{a.msg}</span>
          {overridden && (
            <span className="text-[9px] uppercase tracking-wider bg-slate-800 text-white px-1.5 py-0.5 rounded font-bold">Overridden</span>
          )}
        </div>
        {a.guideline && (
          <GuidelineLink
            text={a.guideline}
            url={guidelineUrl}
            testid={`guidance-alert-guideline-link-${a.rule_id || "ai"}`}
          />
        )}
        {a.medicine && <div className="text-[10px] opacity-75 mt-0.5">Medicine: {a.medicine}</div>}
        {overridden && overridden.reason && (
          <div className="text-[10px] italic text-slate-700 mt-1 bg-white/70 border border-slate-200 rounded px-1.5 py-1">
            <strong>Overridden</strong> by {overridden.user_email || "doctor"}: {overridden.reason}
          </div>
        )}
      </div>
      {isBlockOrMajor && !overridden && onOverride && (
        <button
          onClick={onOverride}
          className="text-[10px] shrink-0 px-2 py-1 rounded border border-current opacity-80 hover:opacity-100"
          data-testid={`guidance-override-${a.rule_id || "ai"}`}
        >
          <ShieldOff size={11} className="inline mr-0.5" /> Override
        </button>
      )}
    </div>
  );
}

// PROV: Phase-1 observational provenance (fail-open)
import { provLog } from "../lib/provenance";
import { genId as provGenId, recId as provRecId, medEventFields } from "../lib/provenanceCore";

export default function GuidancePanel({
  patientId, patient, vitals, bmi, labs, allergyFlags, comorbidities,
  diagnosisPrimary, diagnosisSecondary, chief, historyText, medicines, onAddMolecule, onAddNextInvestigation, onAppendAdvice,
}) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [ai, setAi] = useState(null);
  const [err, setErr] = useState("");
  const [overrides, setOverrides] = useState([]); // list of {rule_id, reason, timestamp, user_email, ...}
  const [showHistory, setShowHistory] = useState(false);
  const [ovrTarget, setOvrTarget] = useState(null); // alert being overridden
  const [ovrReason, setOvrReason] = useState("");
  const [ovrSaving, setOvrSaving] = useState(false);
  const [guidelines, setGuidelines] = useState([]); // active guideline list w/ URLs
  const gSessRef = React.useRef(null); // PROV: guidance session id per panel open
  const [aiReqId, setAiReqId] = useState(null); // PROV: backend provenance request id for the Gemini call

  // Load overrides history + guideline URL list when dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/settings/guidelines");
        setGuidelines(r.data.guidelines || []);
      } catch (e) { console.debug("[silent]", e); }
    })();
    if (!patientId) return;
    (async () => {
      try {
        const r = await api.get(`/patients/${patientId}/overrides`);
        setOverrides(r.data.overrides || []);
      } catch (e) { console.debug("[silent]", e); }
    })();
  }, [open, patientId]);

  const overrideOf = (a) => overrides.find((o) => o.rule_id === (a.rule_id || `ai-${a.msg?.slice(0, 40)}`));
  const openOverride = (a) => { setOvrTarget(a); setOvrReason(""); };
  const saveOverride = async () => {
    if (!ovrTarget) return;
    const reason = ovrReason.trim();
    if (!reason) { toast.error("Reason is required"); return; }
    setOvrSaving(true);
    try {
      const r = await api.post("/audit/override", {
        patient_id: patientId,
        rule_id: ovrTarget.rule_id || `ai-${(ovrTarget.msg || "").slice(0, 40)}`,
        level: ovrTarget.level,
        message: ovrTarget.msg,
        medicine: ovrTarget.medicine || "",
        guideline: ovrTarget.guideline || "",
        source: ovrTarget.source || "rules",
        reason,
      });
      setOverrides([r.data.override, ...overrides]);
      toast.success("Override recorded in audit log");
      setOvrTarget(null);
      setOvrReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record override");
    } finally { setOvrSaving(false); }
  };

  // Build inputs used by both the deterministic engine and the AI call.
  const profile = {
    age: patient?.age, sex: patient?.sex,
    weight: vitals?.weight, waist: vitals?.waist, sbp: vitals?.sbp, dbp: vitals?.dbp,
    pulse: vitals?.pulse, spo2: vitals?.spo2, bmi,
  };
  const rules = runClinicalRules({ profile, labs, medicines, allergyFlags, comorbidities, diagnosisPrimary });
  const sortedAlerts = [...rules.alerts].sort((a, b) => (LEVEL_ORDER[a.level] || 9) - (LEVEL_ORDER[b.level] || 9));

  const runAI = async () => {
    setRunning(true); setErr(""); setAi(null);
    try {
      const r = await api.post("/ai/clinical-guidance", {
        patient_id: patientId, profile, labs, allergyFlags, comorbidities,
        diagnosisPrimary, diagnosisSecondary, chief, history: historyText, medicines,
      }, { timeout: 120000 });
      setAi(r.data.guidance || {});
      setAiReqId(r.data.provenance_request_id || null); // PROV
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed");
      toast.error(e.response?.data?.detail || "Guidance failed");
    } finally { setRunning(false); }
  };

  const addMol = (mol) => {
    const brand = mol.indian_brands?.[0] || {};
    const dose = mol.typical_dose || "";
    // Parse "10 mg OD" into dose + frequency
    let freq = "";
    const freqMatch = String(dose).match(/\b(OD|BD|TDS|QID|HS|Q4H|Q6H|Q8H|Q12H|Once weekly|Twice weekly|Once monthly|Stat|SOS)\b/i);
    if (freqMatch) freq = freqMatch[1].toUpperCase() === freqMatch[1] ? freqMatch[1] : freqMatch[1];
    // MT-11B.7: the suggested dose is a PATIENT dose — never the product
    // strength. Strength comes only from the brand master; for insulin it is
    // the concentration (U-100/U-200/U-300) when known, else blank.
    const insulin = isInsulinRow({ generic_name: mol.molecule, brand_name: brand.name });
    const doseText = (String(dose).match(/\d+(?:\.\d+)?\s*(?:mg|mcg|µg|g|ml|iu|units?|u)\b/i) || [""])[0].trim();
    const brandStrength = String(brand.strength || "");
    const strength = insulin
      ? (insulinConcentration(brandStrength) || "")
      : (isUnitDoseAsStrength(brandStrength, { generic_name: mol.molecule }) ? "" : brandStrength);
    const parsed = parseAmount(doseText);
    const row = {
      brand_name: brand.name || mol.molecule,
      generic_name: mol.molecule,
      composition: mol.molecule,
      strength,
      concentration: insulin ? (insulinConcentration(brandStrength) || undefined) : undefined,
      dose: doseText || dose,
      dose_unit: parsed.unit || undefined,
      route: insulin ? "SC" : "Oral",
      frequency: freq || "OD",
      timing: "",
      duration: "",
      quantity: "",
      instructions: mol.rationale ? `AI: ${mol.rationale}` : "",
      product_unverified: isCombinedProductName(brand.name || mol.molecule) || undefined,
    };
    // PROV: origin GEMINI captured at the moment of physician acceptance
    const provRow = { ...row, _prov: { origin: "GEMINI", detail: "ask_gemini", gemini_request_id: aiReqId, guidance_session_id: gSessRef.current } };
    provLog({ event_type: "GEMINI_CANDIDATE_ACCEPTED", guidance_session_id: gSessRef.current, gemini_request_id: aiReqId, patient_internal_id: patientId, physician_action: "ACCEPTED", medicine: medEventFields(provRow), evidence_text: mol.rationale || null, source_guideline: mol.guideline || null });
    onAddMolecule && onAddMolecule(provRow);
    toast.success(`Added ${row.brand_name} for review`);
  };

  // "Rx-note" chip — copy the AI/rules push suggestion's full clinical
  // reasoning into the Advice/Rx notes. Renders a compact one-liner:
  //   • [Insulin] Basal Glargine — 0.1-0.2 U/kg HS (Why: FBS > 250 mg/dL; ADA 2024)
  // Preserves the source (rules / algorithm) so the printed Rx carries an
  // auditable trail of why every suggestion was accepted.
  const pushReasoning = (s, kind) => {
    if (!onAppendAdvice) return;
    const parts = [];
    const label = s.regimen || s.drug || s.rx?.generic_name || s.rx?.brand_name || "Suggestion";
    parts.push(`• [${kind}] ${label}`);
    if (s.start) parts.push(`— start ${s.start}`);
    else if (s.dose) parts.push(`— ${s.dose}`);
    if (s.titration) parts.push(`; titrate ${s.titration}`);
    if (s.rationale) parts.push(`(Why: ${s.rationale})`);
    if (s.citations?.length) parts.push(`[${s.citations.slice(0, 2).join(" · ")}]`);
    onAppendAdvice(parts.join(" "));
    toast.success("Reasoning copied to Advice / Rx");
  };
  // Small reusable Rx-note button. Sits next to every "Prescribe this".
  const RxNoteBtn = ({ s, kind, testid }) => (
    !onAppendAdvice ? null : (
      <button
        onClick={() => pushReasoning(s, kind)}
        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1"
        data-testid={testid}
        title="Copy this AI reasoning line into the Advice / Rx notes for the printed prescription"
      >
        <FileText size={10} /> Rx-note
      </button>
    )
  );

  const aiSuggestions = ai?.suggested_molecules || [];

  // #9: sort chronic groups by how often the doctor accepts their cards
  const CNT_KEY = "card_accept_counts_v1";
  const loadCounts = () => { try { return JSON.parse(localStorage.getItem(CNT_KEY) || "{}"); } catch { return {}; } };
  const bumpCount = (k) => { try { const c = loadCounts(); c[k] = (c[k] || 0) + 1; localStorage.setItem(CNT_KEY, JSON.stringify(c)); } catch (e) { console.debug("[silent]", e); } };
  const orderedChronic = React.useMemo(() => {
    const c = loadCounts();
    return [...CHRONIC_ORDER].sort((a, b) => (c[b.key] || 0) - (c[a.key] || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #6 card upgrade: copy the card's titration plan into the drug's
  // instructions box so the week-by-week schedule prints on the Rx.
  const withTitration = (s, rxRow) => {
    if (!s?.titration || !rxRow) return rxRow;
    const plan = `Plan: ${s.titration}`.slice(0, 180);
    if ((rxRow.instructions || "").includes(s.titration.slice(0, 30))) return rxRow;
    return { ...rxRow, instructions: [rxRow.instructions, plan].filter(Boolean).join(" · ") };
  };

  // -------- Dose Algorithm suggestions (deterministic, no LLM needed) --------
  // Feeds a rules-based engine (see /lib/doseAlgorithms.js) that turns the
  // patient's age, weight, eGFR, HbA1c and comorbidity chips into concrete
  // starting doses and titration schedules for insulin AND GLP-1 injections,
  // with every claim cited to ADA / RSSDI / KDIGO or the trial name.
  const doseCtx = React.useMemo(() => {
    const scr = labs?.creatinine;
    const age = parseFloat(patient?.age);
    const egfr = (scr && age) ? ckdEpi2021(scr, age, patient?.sex) : null;
    return {
      age: Number.isFinite(age) ? age : null,
      sex: (patient?.sex || "").toLowerCase(),
      weight: parseFloat(vitals?.weight) || null,
      height: parseFloat(vitals?.height) || null,
      egfr,
      hba1c: parseFloat(labs?.hba1c) || null,
      fbs: parseFloat(labs?.fbs) || null,
      ppbs: parseFloat(labs?.ppbs) || null,
      known: comorbidities || [],
      currentMeds: medicines || [],
      pregnant: (allergyFlags || []).some((f) => /pregnan/i.test(f))
        || /pregnan|gestational|\bgdm\b|antenatal/i.test(`${(comorbidities || []).join(" ")} ${diagnosisPrimary || ""} ${diagnosisSecondary || ""}`),
      allergyFlags: allergyFlags || [],
      diagnosisPrimary: diagnosisPrimary || "",
      diagnosisSecondary: diagnosisSecondary || "",
      vitals: vitals || {},
      sbp: parseFloat(vitals?.sbp) || null,
      dbp: parseFloat(vitals?.dbp) || null,
      labs: labs || {},
    };
  }, [patient, vitals, labs, comorbidities, medicines, allergyFlags, diagnosisPrimary, diagnosisSecondary]);
  const insulinPlan = React.useMemo(() => suggestInsulin(doseCtx), [doseCtx]);
  const glp1Plan = React.useMemo(() => suggestGLP1(doseCtx), [doseCtx]);
  const pushPlans = React.useMemo(() => suggestAllPushDrugs(doseCtx), [doseCtx]);
  const chronicPlans = React.useMemo(() => {
    const extCtx = { ...doseCtx, bmi };
    return suggestAllChronicConditions(extCtx);
  }, [doseCtx, bmi]);
  const anyChronic = CHRONIC_ORDER.some(({ key }) => {
    const p = chronicPlans[key];
    return p?.recommend && p?.suggestions?.length > 0;
  });

  // PROV: record the deterministic candidates actually shown, once per panel open (observational only)
  useEffect(() => {
    if (!open) return;
    gSessRef.current = provGenId();
    try {
      const cands = [];
      if (insulinPlan.recommend) (insulinPlan.suggestions || []).forEach((s, i) => cands.push({ recommendation_id: provRecId(gSessRef.current, "insulin", null, i, s.drug || s.regimen), section: "insulin", molecule: s.drug || s.regimen || "", dose: s.dose || "", titration: s.titration || "", rationale: s.rationale || "", citations: s.citations || [] }));
      if (glp1Plan.recommend) (glp1Plan.suggestions || []).forEach((s, i) => cands.push({ recommendation_id: provRecId(gSessRef.current, "glp1", null, i, s.drug || s.regimen), section: "glp1", molecule: s.drug || s.regimen || "", dose: s.start || s.dose || "", titration: s.titration || "", rationale: s.rationale || "", citations: s.citations || [] }));
      Object.entries(pushPlans || {}).forEach(([key, p]) => { if (p?.recommend) (p.suggestions || []).forEach((s, i) => cands.push({ recommendation_id: provRecId(gSessRef.current, "push", key, i, s.drug || s.regimen), section: "push", rule_key: key, molecule: s.drug || s.regimen || "", dose: s.dose || "", titration: s.titration || "", rationale: s.rationale || "", citations: s.citations || [] })); });
      Object.entries(chronicPlans || {}).forEach(([key, p]) => { if (p?.recommend) (p.suggestions || []).forEach((s, i) => cands.push({ recommendation_id: provRecId(gSessRef.current, "chronic", key, i, s.drug || s.regimen), section: "chronic", rule_key: key, molecule: s.drug || s.regimen || "", dose: s.dose || "", titration: s.titration || "", rationale: s.rationale || "", citations: s.citations || [] })); });
      if (cands.length) provLog({ event_type: "CG_CANDIDATES", guidance_session_id: gSessRef.current, patient_internal_id: patientId, candidates: cands, count: cands.length, mechanism: "DETERMINISTIC" });
    } catch (e) { console.debug("[prov-fail-open]", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // PROV: tag deterministic-card acceptance; the guard path (onAddMolecule) is unchanged
  const provAccept = (rx, section, key, index, s) => {
    const rid = provRecId(gSessRef.current || "na", section, key, index, s?.drug || s?.regimen || rx?.generic_name);
    provLog({ event_type: "CG_CANDIDATE_ACCEPTED", guidance_session_id: gSessRef.current, patient_internal_id: patientId, recommendation_id: rid, physician_action: "ACCEPTED", medicine: medEventFields(rx), source_guideline: (s?.citations || []).join(" · ") || null, evidence_text: s?.rationale || null });
    return onAddMolecule({ ...rx, _prov: { origin: "CLINICAL_GUIDANCE", detail: section, recommendation_id: rid, guidance_session_id: gSessRef.current } });
  };
  const pushOrder = [
    { key: "metformin", label: "Metformin (T2DM first-line)" },
    { key: "sglt2", label: "SGLT2 inhibitors" },
    { key: "dpp4", label: "DPP-4 inhibitors (Gliptins)" },
    { key: "sulfonylurea", label: "Sulfonylurea (Gliclazide / Glimepiride)" },
    { key: "pioglitazone", label: "Pioglitazone (TZD)" },
    { key: "statin", label: "Statin (Lipid-lowering)" },
    { key: "ezetimibe", label: "Ezetimibe (Statin add-on)" },
    { key: "arni", label: "ARNI — Sacubitril + Valsartan (HFrEF Class I)" },
    { key: "aceiarb", label: "ACE-inhibitor / ARB (RAASi)" },
    { key: "amlodipine", label: "Amlodipine (CCB)" },
    { key: "thiazide", label: "Thiazide diuretic" },
    { key: "beta_blocker", label: "Beta-blocker (HFrEF / Post-MI / AF)" },
    { key: "mra_hf", label: "MRA for HFrEF (Spironolactone / Eplerenone)" },
    { key: "loop_diuretic", label: "Loop diuretic (HF congestion)" },
    { key: "aspirin", label: "Aspirin (Secondary prevention)" },
    { key: "finerenone", label: "Finerenone (DKD add-on MRA)" },
    { key: "ppi", label: "PPI — Pantoprazole (GI prophylaxis)" },
    { key: "vitamin_d", label: "Vitamin D3 (Deficiency / osteoporosis)" },
    { key: "levothyroxine", label: "Levothyroxine (Hypothyroidism)" },
  ];
  const anyPush = pushOrder.some(({ key }) => {
    const p = pushPlans[key];
    return p?.recommend && (p?.suggestions?.length > 0 || p?.cautions?.length > 0);
  });
  const showDoseSection = insulinPlan.recommend || glp1Plan.recommend || anyPush || anyChronic;
  const aiSafety = ai?.safety_flags || [];
  const aiLabs = ai?.required_labs_before_prescription || [];
  const aiLifestyle = ai?.lifestyle || [];
  const combinedAlerts = [
    ...sortedAlerts.map((a) => ({ ...a, source: "rules" })),
    ...aiSafety.map((a) => ({ ...a, source: "ai" })),
  ].sort((a, b) => (LEVEL_ORDER[a.level] || 9) - (LEVEL_ORDER[b.level] || 9));

  const combinedLabs = [
    ...rules.requiredLabs.map((l) => ({ lab: l.lab, reason: l.reason, source: "rules" })),
    ...aiLabs.map((l) => ({ lab: l.lab, reason: l.reason, source: "ai" })),
  ];

  const criticalCount = combinedAlerts.filter((a) => a.level === "block" || a.level === "major").length;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-gradient-to-r from-pink-600 to-fuchsia-600 hover:from-pink-700 hover:to-fuchsia-700 text-white gap-2"
        data-testid="guidance-open"
      >
        <Stethoscope size={14} /> Clinical Guidance
        {criticalCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-red-700 text-[10px] font-bold" data-testid="guidance-alert-badge">{criticalCount}</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope size={16} className="text-pink-600" /> Clinical guidance for {patient?.name || "this patient"}
              <span className="text-[10px] font-normal text-slate-500">Rules engine + Gemini 3 Pro guideline assistant</span>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 pr-1 space-y-4">
            {/* Snapshot strip */}
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
              <div><span className="text-slate-500">Age/Sex</span> {patient?.age || "—"} / {patient?.sex || "—"}</div>
              <div><span className="text-slate-500">BMI</span> {bmi || "—"}</div>
              <div><span className="text-slate-500">BP</span> {vitals?.sbp || "—"}/{vitals?.dbp || "—"}</div>
              <div><span className="text-slate-500">Dx</span> {diagnosisPrimary || "—"}</div>
              <div><span className="text-slate-500">eGFR</span> {labs?.egfr || "—"}</div>
              <div><span className="text-slate-500">HbA1c</span> {labs?.hba1c || "—"}</div>
              <div><span className="text-slate-500">LDL</span> {labs?.ldl || "—"}</div>
              <div><span className="text-slate-500">Creatinine</span> {labs?.creatinine || "—"}</div>
            </div>

            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-md p-2.5 text-amber-900 flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <div>
                <strong>Decision support only.</strong> Every molecule, dose and interaction alert must be independently verified by the prescribing doctor before any medicine is added to the prescription. AI outputs are marked as drafts.
              </div>
            </div>

            {/* Dose algorithm suggestions (insulin + GLP-1) */}
            {showDoseSection && (
              <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3" data-testid="guidance-dose-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1.5">
                    <FlaskConical size={11} /> Injectable dose suggestions
                    <span className="text-[9px] normal-case tracking-normal text-emerald-700/70 font-normal">rules-based · ADA / RSSDI / KDIGO</span>
                  </div>
                  {(insulinPlan.missing?.length > 0 || glp1Plan.missing?.length > 0) && (
                    <span
                      className="text-[10px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                      title={`Missing inputs: ${[...(insulinPlan.missing || []), ...(glp1Plan.missing || [])].join(", ")}`}
                    >
                      partial data
                    </span>
                  )}
                </div>

                {/* Insulin */}
                {insulinPlan.recommend && insulinPlan.suggestions.length > 0 && (
                  <div className="mb-3" data-testid="guidance-insulin-block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Insulin</div>
                    <div className="space-y-1.5">
                      {insulinPlan.suggestions.map((s, i) => (
                        <div key={i} className={`border rounded-md p-2 text-xs ${s.recommended ? "border-emerald-300 bg-white" : "border-slate-200 bg-white/60"}`} data-testid={`insulin-suggest-${i}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {s.recommended && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">Recommended</span>}
                            <span className="font-semibold text-slate-900">{s.regimen}</span>
                            {s.rx && onAddMolecule && (
                              <button
                                onClick={() => { const ok = provAccept(s.rx, "insulin", null, i, s); if (ok !== false) toast.success(`Added "${s.rx.generic_name}" to prescription`); }}
                                className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-pink-600 text-white hover:bg-pink-700 flex items-center gap-1"
                                data-testid={`insulin-prescribe-${i}`}
                                title="Drop this dose row into the Prescription table with dose & frequency pre-filled"
                              >
                                <Plus size={10} /> Prescribe this
                              </button>
                            )}
                            <RxNoteBtn s={s} kind="Insulin" testid={`insulin-rxnote-${i}`} />
                          </div>
                          {s.drug && <div className="text-[11px] text-slate-700 mt-0.5"><strong>Drug:</strong> {s.drug}</div>}
                          <div className="text-[11px] text-pink-800 font-medium mt-0.5"><strong>Dose:</strong> {s.dose}</div>
                          {s.titration && <div className="text-[11px] text-slate-600 mt-0.5"><strong>Titrate:</strong> {s.titration}</div>}
                          {s.rationale && <div className="text-[10px] italic text-slate-500 mt-0.5">Why: {s.rationale}</div>}
                          {s.cautions?.length > 0 && (
                            <ul className="text-[10px] text-amber-800 mt-1 space-y-0.5">
                              {s.cautions.map((c, k) => <li key={k} className="flex items-start gap-1"><AlertTriangle size={9} className="mt-0.5 shrink-0" /> {c}</li>)}
                            </ul>
                          )}
                          {s.citations?.length > 0 && (
                            <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1"><BookOpen size={9} /> {s.citations.join(" · ")}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* GLP-1 */}
                {glp1Plan.recommend && glp1Plan.suggestions.length > 0 && (
                  <div data-testid="guidance-glp1-block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">GLP-1 receptor agonists</div>
                    <div className="space-y-1.5">
                      {glp1Plan.suggestions.map((s, i) => (
                        <div key={i} className={`border rounded-md p-2 text-xs ${s.recommended ? "border-emerald-300 bg-white" : "border-slate-200 bg-white/60"}`} data-testid={`glp1-suggest-${i}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {s.recommended && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">Recommended</span>}
                            <span className="font-semibold text-slate-900">{s.drug}</span>
                            {s.rx && onAddMolecule && (
                              <button
                                onClick={() => { const ok = provAccept(s.rx, "glp1", null, i, s); if (ok !== false) toast.success(`Added "${s.rx.generic_name}" to prescription`); }}
                                className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-pink-600 text-white hover:bg-pink-700 flex items-center gap-1"
                                data-testid={`glp1-prescribe-${i}`}
                                title="Drop this GLP-1 into the Prescription table with titration schedule pre-filled"
                              >
                                <Plus size={10} /> Prescribe this
                              </button>
                            )}
                            <RxNoteBtn s={s} kind="GLP-1" testid={`glp1-rxnote-${i}`} />
                          </div>
                          <div className="text-[11px] text-pink-800 font-medium mt-0.5"><strong>Start:</strong> {s.start}</div>
                          {s.titration && <div className="text-[11px] text-slate-600 mt-0.5"><strong>Titrate:</strong> {s.titration}</div>}
                          {s.target && <div className="text-[11px] text-slate-700 mt-0.5"><strong>Target:</strong> {s.target}</div>}
                          {s.rationale && <div className="text-[10px] italic text-slate-500 mt-0.5">Why: {s.rationale}</div>}
                          {s.cautions?.length > 0 && (
                            <ul className="text-[10px] text-amber-800 mt-1 space-y-0.5">
                              {s.cautions.map((c, k) => <li key={k} className="flex items-start gap-1"><AlertTriangle size={9} className="mt-0.5 shrink-0" /> {c}</li>)}
                            </ul>
                          )}
                          {s.citations?.length > 0 && (
                            <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1"><BookOpen size={9} /> {s.citations.join(" · ")}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {glp1Plan.reason && !glp1Plan.recommend && (
                  <div className="text-[10px] text-slate-500 italic mt-1">GLP-1 skipped: {glp1Plan.reason}</div>
                )}

                {/* Preventive & Chronic Rx push suggestions */}
                {anyPush && (
                  <div className="mt-3 pt-3 border-t border-emerald-200" data-testid="guidance-push-block">
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1.5">
                        <Pill size={11} /> Preventive & chronic Rx (one-tap push)
                      </div>
                      <span className="text-[9px] normal-case tracking-normal text-emerald-700/70 font-normal">rules-based · ADA / AHA/ACC / KDIGO / ATA</span>
                    </div>
                    {pushOrder.map(({ key, label }) => {
                      const plan = pushPlans[key];
                      if (!plan?.recommend) return null;
                      const isBlocked = plan.suggestions?.length === 0 && plan.cautions?.length > 0;
                      if (!plan.suggestions?.length && !isBlocked) return null;
                      return (
                        <div key={key} className="mb-3 last:mb-0" data-testid={`push-block-${key}`}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1.5">
                            {label}
                            {isBlocked && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">Blocked</span>}
                          </div>
                          {isBlocked && (
                            <div className="border border-red-200 bg-red-50 rounded-md p-2 text-xs" data-testid={`push-blocked-${key}`}>
                              <div className="text-[11px] text-red-900 font-medium flex items-center gap-1"><Ban size={11} /> Eligible on paper but contraindicated — see reasons below.</div>
                              {plan.reason && <div className="text-[10px] italic text-red-700 mt-1">{plan.reason}</div>}
                              <ul className="text-[10px] text-red-800 mt-1.5 space-y-0.5">
                                {plan.cautions.map((c, k) => <li key={k} className="flex items-start gap-1"><AlertTriangle size={9} className="mt-0.5 shrink-0" /> {c}</li>)}
                              </ul>
                            </div>
                          )}
                          <div className="space-y-1.5">
                            {plan.suggestions.map((s, i) => (
                              <div
                                key={i}
                                className={`border rounded-md p-2 text-xs ${s.recommended ? "border-emerald-300 bg-white" : "border-slate-200 bg-white/60"}`}
                                data-testid={`push-suggest-${key}-${i}`}
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {s.recommended && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">Recommended</span>}
                                  <span className="font-semibold text-slate-900">{s.regimen || s.drug}</span>
                                  {s.rx && onAddMolecule && (
                                    <button
                                      onClick={() => { const ok = provAccept(s.rx, "push", key, i, s); if (ok !== false) toast.success(`Added "${s.rx.generic_name}" to prescription`); }}
                                      className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-pink-600 text-white hover:bg-pink-700 flex items-center gap-1"
                                      data-testid={`push-prescribe-${key}-${i}`}
                                      title="Drop this dose row into the Prescription table with dose & frequency pre-filled"
                                    >
                                      <Plus size={10} /> Prescribe this
                                    </button>
                                  )}
                                  <RxNoteBtn s={s} kind={key} testid={`push-rxnote-${key}-${i}`} />
                                </div>
                                {s.drug && s.regimen && s.drug !== s.regimen && (
                                  <div className="text-[11px] text-slate-700 mt-0.5"><strong>Drug:</strong> {s.drug}</div>
                                )}
                                <div className="text-[11px] text-pink-800 font-medium mt-0.5"><strong>Dose:</strong> {s.dose}</div>
                                {s.titration && <div className="text-[11px] text-slate-600 mt-0.5"><strong>Titrate:</strong> {s.titration}</div>}
                                {s.rationale && <div className="text-[10px] italic text-slate-500 mt-0.5">Why: {s.rationale}</div>}
                                {s.cautions?.length > 0 && (
                                  <ul className="text-[10px] text-amber-800 mt-1 space-y-0.5">
                                    {s.cautions.map((c, k) => <li key={k} className="flex items-start gap-1"><AlertTriangle size={9} className="mt-0.5 shrink-0" /> {c}</li>)}
                                  </ul>
                                )}
                                {s.citations?.length > 0 && (
                                  <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1"><BookOpen size={9} /> {s.citations.join(" · ")}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Condition-Specific Chronic Rx (ported from jm_suggest_chronic.rules.js) */}
                {anyChronic && (
                  <div className="mt-3 pt-3 border-t border-emerald-200" data-testid="guidance-chronic-block">
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1.5">
                        <Pill size={11} /> Condition-Specific Chronic Rx
                      </div>
                      <span className="text-[9px] normal-case tracking-normal text-emerald-700/70 font-normal">
                        rules-based · GINA / GOLD / ACR / AAN / ATA / EULAR · triggered by Known Case + Primary/Secondary Dx
                      </span>
                    </div>
                    {orderedChronic.map(({ key, label }) => {
                      const plan = chronicPlans[key];
                      if (!plan?.recommend || !plan.suggestions?.length) return null;
                      return (
                        <div key={key} className="mb-3 last:mb-0" data-testid={`chronic-block-${key}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                              {label}
                            </div>
                            {(() => {
                              const bundle = plan.suggestions.filter((s) => s.rx && s.recommended !== false);
                              if (bundle.length < 2 || !onAddMolecule) return null;
                              return (
                                <button
                                  onClick={() => {
                                    const results = bundle.map((s) => provAccept(withTitration(s, adjustRxForContext(s.rx, doseCtx).rx), "chronic", key, plan.suggestions.indexOf(s), s));
                                    bumpCount(key);
                                    const added = results.filter((r) => r !== false).length;
                                    const held = bundle.length - added;
                                    if (added) toast.success(`Added ${added} medicine${added > 1 ? "s" : ""} from "${label}"${held ? ` — ${held} held for safety review` : ""}`);
                                    else toast.warning(`All ${bundle.length} held for safety review — see dialog`);
                                  }}
                                  className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-pink-600 text-pink-700 hover:bg-pink-50 flex items-center gap-1"
                                  data-testid={`chronic-accept-all-${key}`}
                                  title="Add every recommended medicine in this group to the prescription in one tap"
                                >
                                  <Plus size={10} /> Accept all {bundle.length}
                                </button>
                              );
                            })()}
                          </div>
                          <div className="space-y-1.5">
                            {plan.suggestions.map((s, i) => {
                              const adj = s.rx ? adjustRxForContext(s.rx, doseCtx) : { rx: s.rx, note: null };
                              return (
                              <div
                                key={i}
                                className={`border rounded-md p-2 text-xs ${s.recommended ? "border-emerald-300 bg-white" : "border-slate-200 bg-white/60"}`}
                                data-testid={`chronic-suggest-${key}-${i}`}
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {s.recommended && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">Recommended</span>}
                                  {adj.note && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white" title={adj.note} data-testid={`chronic-renal-adj-${key}-${i}`}>Renal-adjusted</span>}
                                  <span className="font-semibold text-slate-900">{s.regimen || s.drug}</span>
                                  {s.rx && onAddMolecule && (
                                    <button
                                      onClick={() => {
                                        const ok = provAccept(withTitration(s, adj.rx), "chronic", key, i, s);
                                        bumpCount(key);
                                        if (ok !== false) toast.success(`Added "${adj.rx.generic_name}" to prescription`);
                                      }}
                                      className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-pink-600 text-white hover:bg-pink-700 flex items-center gap-1"
                                      data-testid={`chronic-prescribe-${key}-${i}`}
                                      title="Drop this dose row into the Prescription table with dose & frequency pre-filled"
                                    >
                                      <Plus size={10} /> Prescribe this
                                    </button>
                                  )}
                                  <RxNoteBtn s={s} kind={key} testid={`chronic-rxnote-${key}-${i}`} />
                                </div>
                                {s.drug && s.regimen && s.drug !== s.regimen && (
                                  <div className="text-[11px] text-slate-700 mt-0.5"><strong>Drug:</strong> {s.drug}</div>
                                )}
                                <div className="text-[11px] text-pink-800 font-medium mt-0.5">
                                  <strong>Dose:</strong> {adj.note ? <span><s className="text-slate-400">{s.dose}</s> <span className="text-amber-700 font-semibold">{adj.rx.dose} {adj.rx.frequency} — {adj.note}</span></span> : s.dose}
                                </div>
                                {s.titration && <div className="text-[11px] text-slate-600 mt-0.5"><strong>Titrate:</strong> {s.titration}</div>}
                                {s.rationale && <div className="text-[10px] italic text-slate-500 mt-0.5">Why: {s.rationale}</div>}
                                {s.cautions?.length > 0 && (
                                  <ul className="text-[10px] text-amber-800 mt-1 space-y-0.5">
                                    {s.cautions.map((c, k) => <li key={k} className="flex items-start gap-1"><AlertTriangle size={9} className="mt-0.5 shrink-0" /> {c}</li>)}
                                  </ul>
                                )}
                                {s.citations?.length > 0 && (
                                  <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1 flex-wrap">
                                    <BookOpen size={9} />
                                    {s.citations.map((c, k) => (
                                      <button
                                        key={k}
                                        onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(`${c} ${s.drug || s.regimen || ""} guideline`)}`, "_blank")}
                                        className="underline decoration-dotted hover:text-pink-600"
                                        title="Open this guideline reference in a new tab"
                                        data-testid={`chronic-cite-${key}-${i}-${k}`}
                                      >{c}</button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Safety flags */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                  <ShieldAlert size={12} /> Safety flags ({combinedAlerts.length})
                  {overrides.length > 0 && (
                    <span className="text-[9px] text-slate-500 normal-case tracking-normal ml-1">
                      · {overrides.length} recorded override{overrides.length > 1 ? "s" : ""}
                    </span>
                  )}
                </h3>
                {overrides.length > 0 && (
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1"
                    data-testid="guidance-history-toggle"
                  >
                    <History size={11} /> {showHistory ? "Hide" : "Show"} history
                  </button>
                )}
              </div>
              {combinedAlerts.length === 0 && (
                <div className="text-xs text-slate-500 border border-slate-200 rounded-md p-2.5 flex items-center gap-2"><ShieldCheck size={13} className="text-emerald-600" /> No hard contraindications detected by rules engine.</div>
              )}
              <div className="space-y-1.5">
                {combinedAlerts.map((a, i) => (
                  <AlertRow
                    key={i}
                    a={a}
                    overridden={overrideOf(a)}
                    onOverride={() => openOverride(a)}
                    guidelineUrl={resolveGuidelineUrl(a.guideline, guidelines)}
                  />
                ))}
              </div>
              {showHistory && overrides.length > 0 && (
                <div className="mt-3 border border-slate-200 rounded-md" data-testid="guidance-history-list">
                  <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    Override history — all recorded in audit log
                  </div>
                  <ul className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {overrides.map((o) => (
                      <li key={o.override_id} className="px-3 py-2 text-[11px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] text-slate-500">{o.rule_id}</span>
                          <span className="text-[9px] uppercase tracking-wider bg-slate-800 text-white px-1.5 py-0.5 rounded font-bold">{o.level}</span>
                          <span className="text-slate-500 text-[10px]">
                            {new Date(o.timestamp).toLocaleString(undefined, { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })} · {o.user_email}
                          </span>
                        </div>
                        <div className="text-slate-700 mt-0.5">{o.message}</div>
                        <div className="text-slate-600 italic mt-0.5">Reason: {o.reason}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Required baseline labs */}
            {combinedLabs.length > 0 && (
              <section>
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5 mb-2">
                  <FlaskConical size={12} /> Baseline labs recommended ({combinedLabs.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {combinedLabs.map((l, i) => (
                    <div key={i} className="border border-slate-200 rounded-md px-3 py-2 text-xs flex items-start justify-between gap-2" data-testid={`guidance-lab-${i}`}>
                      <div>
                        <div className="font-medium text-slate-800">{l.lab}</div>
                        <div className="text-[11px] text-slate-500">{l.reason}</div>
                      </div>
                      {onAddNextInvestigation && (
                        <button
                          onClick={() => { onAddNextInvestigation(l.lab); toast.success(`${l.lab} added to next investigations`); }}
                          className="text-[10px] text-pink-700 hover:underline flex items-center gap-0.5 shrink-0"
                          data-testid={`guidance-lab-add-${i}`}
                        >
                          <Plus size={10} /> add
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* AI section */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                  <Sparkles size={12} className="text-pink-600" /> Guideline-based molecule suggestions
                </h3>
                {!ai && !running && (
                  <Button size="sm" onClick={runAI} className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5 h-7 text-xs" data-testid="guidance-run-ai">
                    <Sparkles size={11} /> Ask Gemini
                  </Button>
                )}
                {ai && !running && (
                  <button onClick={runAI} className="text-[11px] text-pink-700 hover:underline" data-testid="guidance-refresh-ai">Refresh</button>
                )}
              </div>

              {running && (
                <div className="flex flex-col items-center py-8 text-slate-600">
                  <Loader2 size={24} className="animate-spin text-pink-600 mb-2" />
                  <div className="text-xs">Reasoning over ADA / KDIGO / ESC / RSSDI / AASLD guidelines…</div>
                </div>
              )}

              {err && !running && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2.5">{err}</div>
              )}

              {!ai && !running && !err && (
                <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4 text-center">
                  Tap <strong>Ask Gemini</strong> for guideline-grounded molecule suggestions with India brand names, contraindications and baseline-lab requirements.
                </div>
              )}

              {ai && !running && aiSuggestions.length === 0 && (
                <div className="text-xs text-slate-500 border border-slate-200 rounded-md p-2.5">No suggestions returned. Try adding a primary diagnosis or key labs.</div>
              )}

              {ai?.summary && (
                <div className="text-xs italic text-slate-600 border border-slate-200 rounded-md p-2.5 mb-2" data-testid="guidance-summary">
                  <BookOpen size={11} className="inline mr-1 text-slate-400" /> {ai.summary}
                </div>
              )}

              <div className="space-y-2">
                {aiSuggestions.map((s, i) => (
                  <div key={i} className="border border-pink-100 bg-pink-50/40 rounded-md p-3 text-xs" data-testid={`guidance-suggestion-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 flex items-center gap-1"><Pill size={12} className="text-pink-600" /> {s.molecule}</span>
                          {s.drug_class && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{s.drug_class}</span>}
                          {s.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.priority === "first_line" ? "bg-emerald-100 text-emerald-700" : s.priority === "add_on" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{s.priority.replace("_", " ")}</span>}
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">AI Draft</span>
                          <DrugEvidencePopover
                            brand={s.indian_brands?.[0]?.name}
                            generic={s.molecule}
                            composition={s.molecule}
                            egfr={labs?.egfr}
                            testid={`guidance-evidence-${i}`}
                          />
                        </div>
                        <div className="text-slate-700 mt-1">{s.rationale}</div>
                        {s.guideline && (
                          <GuidelineLink
                            text={s.guideline}
                            url={resolveGuidelineUrl(s.guideline, guidelines)}
                            testid={`guidance-suggestion-guideline-link-${i}`}
                          />
                        )}
                        {s.typical_dose && <div className="text-[11px] text-slate-700 mt-1"><strong>Dose:</strong> {s.typical_dose}</div>}
                        {s.indian_brands && s.indian_brands.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            <span className="text-[10px] text-slate-500 mr-1">Indian brands:</span>
                            {s.indian_brands.map((b, bi) => (
                              <span key={bi} className="text-[10px] px-1.5 py-0.5 rounded border border-pink-200 bg-white text-slate-700">
                                {b.name}{b.strength ? ` ${b.strength}` : ""}
                              </span>
                            ))}
                          </div>
                        )}
                        {s.contraindications && s.contraindications.length > 0 && (
                          <div className="mt-1 text-[10px] text-red-700"><strong>CI:</strong> {s.contraindications.join(" · ")}</div>
                        )}
                        {s.baseline_labs && s.baseline_labs.length > 0 && (
                          <div className="mt-1 text-[10px] text-slate-600"><strong>Baseline labs:</strong> {s.baseline_labs.join(", ")}</div>
                        )}
                      </div>
                      {onAddMolecule && (
                        <Button size="sm" onClick={() => addMol(s)} className="bg-pink-600 hover:bg-pink-700 text-white gap-1 h-7 text-[11px] shrink-0" data-testid={`guidance-add-molecule-${i}`}>
                          <ArrowRight size={11} /> Add to Rx
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {aiLifestyle.length > 0 && (
              <section>
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Lifestyle recommendations</h3>
                <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5">
                  {aiLifestyle.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </section>
            )}
          </div>

          <DialogFooter className="pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="guidance-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override reason dialog */}
      <Dialog open={!!ovrTarget} onOpenChange={(v) => { if (!v) setOvrTarget(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldOff size={16} /> Override safety alert
            </DialogTitle>
          </DialogHeader>
          {ovrTarget && (
            <div className="space-y-3 text-sm">
              <div className="bg-red-50 border border-red-200 rounded-md p-2.5 text-xs text-red-900">
                <div className="font-semibold flex items-center gap-1">
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${LEVEL_STYLE[ovrTarget.level]?.chip || "bg-slate-600 text-white"}`}>{ovrTarget.level}</span>
                  {ovrTarget.rule_id && <span className="font-mono text-[10px] opacity-75">{ovrTarget.rule_id}</span>}
                </div>
                <div className="mt-1">{ovrTarget.msg}</div>
                {ovrTarget.medicine && <div className="mt-0.5 text-[11px] opacity-80">Medicine: {ovrTarget.medicine}</div>}
                {ovrTarget.guideline && <div className="mt-0.5 text-[11px] opacity-80">Guideline: {ovrTarget.guideline}</div>}
              </div>
              <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-900">
                You are overriding a <strong>{ovrTarget.level}</strong>-level alert. The reason, your account, and timestamp will be permanently recorded in the audit log and remain attached to this patient chart.
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Reason for override <span className="text-red-600">*</span></label>
                <Textarea
                  value={ovrReason}
                  onChange={(e) => setOvrReason(e.target.value)}
                  placeholder="e.g. Patient tolerating drug well for 2 years; risk vs. benefit accepted after discussion with cardiologist."
                  className="mt-1 text-sm min-h-[90px]"
                  data-testid="guidance-override-reason"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOvrTarget(null)} data-testid="guidance-override-cancel">Cancel</Button>
            <Button
              onClick={saveOverride}
              disabled={!ovrReason.trim() || ovrSaving}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="guidance-override-save"
            >
              {ovrSaving ? <Loader2 size={13} className="animate-spin" /> : <ShieldOff size={13} />}
              Record override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
